import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changeLanguage, languagesForCountries, type AppLanguage } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../hooks/useFeatures';
import { CoverageIndicator } from '../CoverageIndicator/CoverageIndicator';
import { PendingBadge } from '../PendingBadge/PendingBadge';
import { SlowConnectionBanner } from '../SlowConnectionBanner/SlowConnectionBanner';
import './AppHeader.css';

interface Props {
  title?: string;
  showBack?: boolean;
  showLogo?: boolean;
  rightContent?: React.ReactNode;
  beforeCoverage?: React.ReactNode;
  // Se pinta en la fila de abajo, justo ANTES del selector de idioma. Lo usa
  // /positions para su interruptor self-managed.
  beforeLanguage?: React.ReactNode;
}

export function AppHeader({ title, showBack, showLogo = false, rightContent, beforeCoverage, beforeLanguage }: Props) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { username, logout, claims } = useAuth();
  const features = useFeatures();
  // Idiomas ofrecidos: los de los países del usuario solo si su perfil los
  // admite. El flujo clásico se queda con ES/EN, exactamente como hasta ahora.
  const availableLanguages: AppLanguage[] = features.extendedLanguages
    ? languagesForCountries(claims.userCountries)
    : ['es', 'en'];
  // El idioma activo puede traer región ("es-ES"): nos quedamos con el código
  // base y caemos a inglés si no es uno de los ofrecidos.
  const langBase = (i18n.language ?? 'en').split('-')[0] as AppLanguage;
  const currentLang: AppLanguage = availableLanguages.includes(langBase) ? langBase : 'en';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const userBtnRef = useRef<HTMLButtonElement>(null);

  const toggleUserMenu = () => {
    if (userMenuOpen) { setUserMenuOpen(false); return; }
    const r = userBtnRef.current?.getBoundingClientRect();
    if (r) setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setUserMenuOpen(true);
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    // El aviso al back + baja de push (best-effort) y el overlay "Cerrando
    // sesión" viven dentro de logout() (AuthContext). Esperamos a que termine
    // para navegar a /login.
    await logout();
    navigate('/login', { replace: true });
  };
  // Si el PNG del logo no carga (SW antiguo sin precachear el asset, modo
  // avión + caché vacío, etc.), caemos a un texto "ALDAHRA" del mismo
  // estilo en vez de dejar el icono de imagen rota.
  const [logoBroken, setLogoBroken] = useState(false);
  return (
    <>
      <header className="app-header">
        <div className="app-header__left">
          {showBack && (
            <button className="app-header__back" onClick={() => navigate(-1)} aria-label={t('common.back')}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
          )}
          {showLogo && (
            logoBroken ? (
              <span
                className="app-header__logo-fallback"
                onClick={() => navigate('/positions')}
              >
                ALDAHRA
              </span>
            ) : (
              <img
                src="/pwa-192x192.png"
                alt="Aldahra"
                className="app-header__logo"
                width={28}
                height={28}
                decoding="async"
                onClick={() => navigate('/positions')}
                onError={() => setLogoBroken(true)}
                style={{ cursor: 'pointer' }}
              />
            )
          )}
          {title && <h1 className="app-header__title">{title}</h1>}
        </div>
        <div className="app-header__right">
          <div className="app-header__right-top">
            {beforeCoverage}
            <PendingBadge />
            <CoverageIndicator />
            {rightContent}
          </div>
          {username && (
            <div className="app-header__right-bottom">
              {beforeLanguage}
              {/* Cambio rápido de idioma: recuerda la elección de ESTE usuario.
                  Con solo ES/EN se mantienen los dos botones de siempre (los
                  usuarios actuales no ven ningún cambio); con más idiomas se pasa
                  a un desplegable, porque N botones no caben en el header. */}
              {availableLanguages.length <= 2 ? (
                <div className="app-header__lang" role="group" aria-label={t('settings.language.title')}>
                  {availableLanguages.map((lng) => (
                    <button
                      key={lng}
                      type="button"
                      className={`app-header__lang-btn${currentLang === lng ? ' app-header__lang-btn--active' : ''}`}
                      onClick={() => changeLanguage(username, lng)}
                      aria-pressed={currentLang === lng}
                    >
                      {lng.toUpperCase()}
                    </button>
                  ))}
                </div>
              ) : (
                <select
                  className="app-header__lang-select"
                  aria-label={t('settings.language.title')}
                  value={currentLang}
                  onChange={(e) => changeLanguage(username, e.target.value as AppLanguage)}
                >
                  {availableLanguages.map((lng) => (
                    <option key={lng} value={lng}>{lng.toUpperCase()}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                ref={userBtnRef}
                className="app-header__user"
                title={username}
                onClick={toggleUserMenu}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                {username}
              </button>
            </div>
          )}
        </div>
      </header>
      <SlowConnectionBanner />

      {userMenuOpen && menuPos && createPortal(
        <>
          <div className="app-header__menu-backdrop" onClick={() => setUserMenuOpen(false)} />
          <div
            className="app-header__menu"
            role="menu"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <button type="button" className="app-header__menu-item" onClick={handleLogout} role="menuitem">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </svg>
              {t('settings.logout')}
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
